import { useEffect, useState, useRef } from 'react'
import { Container, Grid } from '@material-ui/core'
import { ChatbotConfigurationGeneralView, ChatbotCustomizationView, ChatbotInstructionsView } from '../../views'
import {
  ChatbotService,
  UserService,
  LocalStorageService
} from '../../../services'
import { useTranslation } from 'react-i18next';
import { ModalUtil, SimpleLoading } from '../../pures';
import moment from 'moment';
import { TabPure } from '../../pures'
import { useGlobalState } from '../../../GlobalState'

const ChatbotConfigurationGeneralAction = () => {
  const { t } = useTranslation()
  const { t: transl, i18n } = useTranslation('chatbot', { keyPrefix: 'configuration' })
  const modalUtil = new ModalUtil();
  const { botConfig, setBotConfig, setChatbotList } = useGlobalState()

  const url = process.env.REACT_APP_ETICKET_API;
  const chatbotService = new ChatbotService(url)

  const [loading, setLoading] = useState(true);
  const [initialData, setInitialData] = useState();
  const [assistantTones, setAssistantTones] = useState([]);
  const [assistantTonesRaw, setAssistantTonesRaw] = useState([]);
 
  const [profileImage, setProfileImage] = useState(null)
  const [backgroundImage, setBackgroundImage] = useState(null)

  const [tabSelected, setTabSelected] = useState('generalData')

  const tabs = [
    {
      route: 'generalData',
      title: transl('generalData.title'),
      key: 'generalData.title',
      selected: (tabSelected === 'generalData') ? true : false
    }, {
      route: 'customization',
      title: transl('customization.title'),
      key: 'customization.title',
      selected: (tabSelected === 'customization') ? true : false
    }, {
      route: 'instructions',
      title: transl('instructions.title'),
      key: 'instructions.title',
      selected: (tabSelected === 'instructions') ? true : false
    }
  ]

  const userService = new UserService(url)
  const localStorageService = new LocalStorageService()

  const handleImageChange = async (newImage, type) => {
    try {
      modalUtil.showModal('loading', t('modal.pure.loading.message'), t('modal.pure.loading.title'))
      const response = await chatbotService.uploadImage(botConfig?.id, newImage, type)

      if (type === 'profile') {
        setProfileImage(newImage)
      } else {
        setBackgroundImage(newImage)
      }

      response.imageUrl && modalUtil.showModal('success', null, t('modal.pure.title.success'))
    } catch (error) {
      modalUtil.showModal('error', error.message, t('modal.pure.title.error'))
    }
  }

  const saveData = async (data) => {
    modalUtil.showModal('loading', t('modal.pure.loading.message'), t('modal.pure.loading.title'))

    try {
      let chatbotData = { ...data }
      let auxInitialData = { ...initialData }

      if (tabSelected === 'generalData') {
        chatbotData.allowGeolocation = !!chatbotData.allowGeolocation
        chatbotData.startTime = moment(chatbotData.startTime).format('HH:mm')
        chatbotData.endTime = moment(chatbotData.endTime).format('HH:mm')
        auxInitialData.generalData = data
      }

      if (tabSelected === 'customization') {
        chatbotData.sendAttachment = data.sendAttachment
        chatbotData.activeExpirationDays = data.activeExpirationDays
        chatbotData.clientSupport = data.clientSupport
        chatbotData.fileSize = data.fileSize
        chatbotData.fileExpirationDays = data.fileExpirationDays
        chatbotData.sendFileMax = data.sendFileMax
        chatbotData.sendAudio = !!data.sendAudio
        auxInitialData.customization = data
      }
      if (tabSelected === 'instructions') {
        chatbotData.initialData = data.initialMessage
        chatbotData.additionalInstructions = data.additionalInstructions
        const auxAssistantTone = assistantTonesRaw.find((assistantTone) => data.assistantTone === transl(`instructions.assistantTone.comboboxTypes.${assistantTone.name}`) || data.assistantTone === assistantTone.name)
        chatbotData.assistantToneId = auxAssistantTone.id
        auxInitialData.instructions = data
       }
      
      chatbotData.botConfigId = botConfig?.id

      await chatbotService.updateChatbotConfig(chatbotData)
      setInitialData(auxInitialData)
      modalUtil.showModal('success', null, t('modal.pure.title.success'))
      chatbotData?.chatbotName && handleGetAssistants(chatbotData.chatbotName)
    } catch (error) {
      modalUtil.showModal('error', error.message, t('modal.pure.title.error'))
    }
  }

  const getData = async () => {
    try {
      setLoading(true)
      if (botConfig) {
        let data = await chatbotService.getChatbotConfig(botConfig?.id)
        data = {
          generalData: {
            botId: data?.id,
            chatbotName: data?.chatbotName,
            allowGeolocation: data?.allowGeolocation,
            maxTimeSession: data?.maxTimeSession,
            sessionsPerDay: data?.sessionsPerDay,
            startTime: data?.startTime ? moment(data.startTime, 'HH:mm').toDate() : undefined,
            endTime: data?.endTime ? moment(data.endTime, 'HH:mm').toDate() : undefined
          },
          customization: {
            primaryColor: data?.primaryColor,
            profileImage: data?.assistantImagePath,
            backgroundImage: data?.assistantBackgroundPath,
            sendAttachment: data?.sendAttachment,
            activeExpirationDays: data?.activeExpirationDays,
            fileSize: data?.fileSize,
            fileExpirationDays: data?.fileExpirationDays,
            sendFileMax: data?.sendFileMax,
            clientSupport: data?.clientSupport,
            sendAudio: data?.sendAudio
          },
          instructions: {
            initialMessage: data?.initialMessage,
            additionalInstructions: data?.additionalInstructions,
            assistantTone: data?.assistantTone?.name
          }
        }
  
        setInitialData(data)
        setProfileImage(data.customization.profileImage)
        setBackgroundImage(data.customization.backgroundImage)
        setLoading(false)
      }
    } catch (error) {
      modalUtil.showModal('error', error.message, t('modal.pure.title.error'))
    }
  }

  const getAssistantTones = async () => {
    try {
      let data = await chatbotService.getAssistantTones()
      const assistantTonesNames = data.map(assistantTone => {
        const toneName = assistantTone.name
        return transl(`instructions.assistantTone.comboboxTypes.${toneName}`)
      })
      setAssistantTones(assistantTonesNames)
      setAssistantTonesRaw(data)
    } catch (error) {
      modalUtil.showModal('error', error.message, t('modal.pure.title.error'))
    }
  }

  /**
   * @author Antonio Vazquez
   * @description event to get user assistants
   * @date 26-11-2025
   */
  const handleGetAssistants = async (botname) => {
    try {
      if (botname) {
        const token = localStorageService.getLocalStorageItem('token', true)?.accessToken
        const clientId = localStorageService.getLocalStorageItem('user', true)?.userMetaData?.userA3Cloud.clientId
        const listResponse = await userService.getAssistants(clientId, token)
        setChatbotList(listResponse ? listResponse : [])

        if (typeof setBotConfig === process.env.REACT_APP_TYPEOF_FUNCTION && typeof listResponse === process.env.REACT_APP_TYPEOF_OBJECT) {
          const targetChatbot = listResponse.find(v => v.chatbotName === botname)
          targetChatbot && setBotConfig(targetChatbot)
        }
      }
    } catch (e) {
      console.error(`An error occurred in event getAssistants: ${e}`)
    }
  }

  useEffect(() => {
    getAssistantTones()
  }, [])

  useEffect(() => {
    const assistantTonesNames = assistantTonesRaw.map(assistantTone => {
      const toneName = assistantTone.name
      return transl(`instructions.assistantTone.comboboxTypes.${toneName}`)
    })

    setAssistantTones(assistantTonesNames)
  }, [i18n.language])

  useEffect(() => {
    getData()
  }, [botConfig])

  const updateTabStatus = (tabSelected) => {
    setTabSelected(tabSelected)
  }

  if (loading) return <div className='loading'>
    <SimpleLoading />
  </div>

  return (
    <Container className='view-container'>
      <Grid container spacing={3}>
        <TabPure tabs={tabs} tabStatus={updateTabStatus} language={i18n.language} transl={transl} />

        <Grid item xs={12}>
          <Container maxWidth={'md'}>
            <Grid container spacing={2}>
              {
                tabSelected === 'generalData' && (
                  <ChatbotConfigurationGeneralView
                    initialData={initialData.generalData}
                    saveData={saveData}
                  />
                )
              }
              {
                tabSelected === 'customization' && (
                  <ChatbotCustomizationView
                    initialData={initialData.customization}
                    saveData={saveData}
                    profileImage={profileImage}
                    backgroundImage={backgroundImage}
                    onProfileImageChange={(img) => handleImageChange(img, 'profile')}
                    onBackgroundImageChange={(img) => handleImageChange(img, 'background')}
                  />
                )
              }
              {
                tabSelected === 'instructions' && (
                  <ChatbotInstructionsView
                    initialData={initialData.instructions}
                    saveData={saveData}
                    assistantTones={assistantTones}
                    assistantTonesRaw={assistantTonesRaw}
                  />
                )
              }
            </Grid>
          </Container>
        </Grid>
      </Grid>
    </Container>

  )
}

export default ChatbotConfigurationGeneralAction